import theme from '@style';
import {
  ScrollView,
  StyleSheet,
  TouchableOpacity,
  View,
  ViewStyle,
} from 'react-native';
import Text from '@basicComponents/text';
import LazyImage from '@basicComponents/image';
import React, {useEffect, useMemo, useRef, useState} from 'react';
import Drawer from '../drawer';
import {DrawerRef} from '../drawer/drawer';
import globalStore from '@/services/global.state';
import SelectedSvg from '@/components/svgs/basic/selected';
import {useResponsiveDimensions} from '@/utils';
const iconClose = require('@components/assets/icons/close.webp');
const {font, flex} = theme;

export interface FilterModalOption {
  title: string;
  value: string;
}

/**
 * 筛选hooks，通过remderModal渲染该筛选控件，并通过show来进行显示
 * @param filterList 筛选列表
 * @param filterTitle 筛选标题
 * @param requireInit 是否需要默认选择第一个，默认是
 * @param initialValue 初始值，如果提供则优先使用
 * @returns
 *  @param currentValue 筛选值，涉及查询的根据该值变化进行重新查询
 *  @param renderModal 渲染modal
 *  @param show 通过调用该函数进行展示
 */
export function useFilterModal(
  filterTitle: string,
  filterList: FilterModalOption[] = [],
  requireInit: boolean = true,
  initialValue?: string,
  modalTitle?: string,
) {
  const [currentValue, setCurrentValue] = useState<string>();
  const drawerRef = useRef<DrawerRef>(null);
  const {height: screenHeight} = useResponsiveDimensions();
  const maxListHeight = Math.floor(screenHeight * 0.5);
  const currentTitle = useMemo(() => {
    const _currentItem = filterList.find(
      item => item.value + '' === currentValue + '',
    );
    return _currentItem ? _currentItem.title : '';
  }, [currentValue, filterList]);
  const filterStyles = StyleSheet.create({
    container: {
      paddingHorizontal: 20,
      paddingTop: 0,
      borderTopLeftRadius: theme.borderRadiusSize.xl,
      borderTopRightRadius: theme.borderRadiusSize.xl,
      paddingBottom: globalStore.isAndroid ? theme.paddingSize.l * 2 : 12,
    },
    titleRow: {
      minHeight: 56,
      paddingTop: 20,
      paddingBottom: 12,
    },
    closeButton: {
      padding: 2,
      marginRight: -2,
    },
    list: {
      marginTop: 0,
      maxHeight: maxListHeight,
    },
    listContent: {
      paddingBottom: 12,
    },
    optionItem: {
      minHeight: 48,
      paddingHorizontal: 12,
      borderRadius: theme.borderRadiusSize.xs,
    },
    optionItemActive: {
      backgroundColor: '#F6F7FB',
    },
    optionText: {
      color: '#31373D',
      fontSize: 14,
      lineHeight: 18,
      fontWeight: '500',
    },
    titleText: {
      color: '#31373D',
      fontSize: 14,
      lineHeight: 20,
      fontWeight: '700',
    },
    emptyIcon: {
      width: theme.iconSize.xs,
      height: theme.iconSize.xs,
    },
  });
  const drawerOverlayStyle: ViewStyle | undefined = globalStore.isWeb
    ? ({
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
      } as ViewStyle)
    : undefined;
  useEffect(() => {
    if (filterList.length > 0) {
      // 优先使用initialValue，如果没有则按requireInit逻辑处理
      if (initialValue !== undefined) {
        setCurrentValue(initialValue);
      } else {
        setCurrentValue(requireInit ? filterList[0].value : undefined);
      }
    }
  }, [filterList, requireInit, initialValue]);

  const handleHide = () => {
    drawerRef.current?.close();
  };
  const handleToggle = (value: string) => {
    setCurrentValue(value);
    handleHide();
  };
  const handleShow = (value?: string) => {
    if (value != null) {
      setCurrentValue(value);
    }
    drawerRef.current?.open();
  };

  const subItemsRender = filterList.map((item, index) => (
    <TouchableOpacity
      activeOpacity={1}
      onPress={() => handleToggle(item.value)}
      key={index}>
      <View
        style={[
          filterStyles.optionItem,
          flex.row,
          flex.between,
          flex.centerByCol,
          currentValue === item.value ? filterStyles.optionItemActive : null,
        ]}>
        <Text style={[font.second, font.fs, filterStyles.optionText]}>
          {item.title}
        </Text>
        {currentValue === item.value ? (
          <SelectedSvg
            color={theme.basicColor.primary}
            width={theme.iconSize.xs}
            height={theme.iconSize.xs}
          />
        ) : (
          <View style={filterStyles.emptyIcon} />
        )}
      </View>
    </TouchableOpacity>
  ));

  return {
    renderModal: (
      <Drawer
        ref={drawerRef}
        mode="bottom"
        contentBackgroundColor="#0000"
        overlayBackgroundColor="rgba(49,55,61,0.28)"
        overlayStyle={drawerOverlayStyle}>
        <View style={[filterStyles.container, theme.background.white]}>
          <View
            style={[
              flex.row,
              flex.between,
              flex.centerByCol,
              filterStyles.titleRow,
            ]}>
            <Text style={[filterStyles.titleText]}>
              {modalTitle || filterTitle}
            </Text>
            <TouchableOpacity
              activeOpacity={1}
              onPress={handleHide}
              style={filterStyles.closeButton}>
              <LazyImage
                occupancy={'transparent'}
                imageUrl={iconClose}
                width={24}
                height={24}
              />
            </TouchableOpacity>
          </View>
          {filterList.length > 5 ? (
            <ScrollView
              style={filterStyles.list}
              contentContainerStyle={filterStyles.listContent}
              showsVerticalScrollIndicator={false}>
              {subItemsRender}
            </ScrollView>
          ) : (
            <View style={filterStyles.listContent}>{subItemsRender}</View>
          )}
        </View>
      </Drawer>
    ),
    show: handleShow,
    hide: handleHide,
    currentValue,
    currentTitle,
  };
}
